import { useEffect, useMemo, useRef, useState } from "react";
import {
	readLocalClaim,
	registerClaimWithApi,
} from "../crypto/antiAbuse.ts";
import {
	CLAIM_FEE_EMBR,
	EMBER_CHAIN,
	getTeamClaimAddress,
	CLAIM_STORAGE_KEY,
	CLAIM_COOKIE,
} from "../crypto/config.ts";
import {
	connectWallet,
	isWalletAvailable,
	sendClaimTeamTx,
} from "../crypto/wallet.ts";
import { useDesktopStore } from "../store.ts";
import {
	createDefaultLeague,
	getDefaultTeams,
	loadLeagueList,
	openLeague,
} from "../util/league.ts";
import { toWorker } from "../util/toWorker.ts";
import {
	destroyLeagueSync,
	fetchClaims,
	fetchSnapshot,
	getSyncStatus,
	initLeagueSync,
	onLeagueUpdate,
	onSyncStatus,
	type SyncStatus,
} from "../util/leagueSync.ts";

/** Cities shown before a team name is chosen / approved. */
const FRANCHISE_CITIES: Record<number, string> = {
	0:  "Atlanta",
	1:  "Baltimore",
	2:  "Boston",
	3:  "Chicago",
	4:  "Cincinnati",
	5:  "Cleveland",
	6:  "Dallas",
	7:  "Denver",
	8:  "Detroit",
	9:  "Houston",
	10: "Las Vegas",
	11: "Los Angeles",
	12: "Mexico City",
	13: "Miami",
	14: "Minneapolis",
	15: "Montreal",
	16: "New York",
	17: "Philadelphia",
	18: "Phoenix",
	19: "Pittsburgh",
	20: "Portland",
	21: "Sacramento",
	22: "San Diego",
	23: "San Francisco",
	24: "Seattle",
	25: "St. Louis",
	26: "Tampa Bay",
	27: "Toronto",
	28: "Vancouver",
	29: "Washington",
};

type TeamRow = {
	tid: number;
	region: string;
	name: string;
	abbrev: string;
};

const CRYPTO_LEAGUE_NAME = "World Blockchain Basketball League";

const CONTRACT_LIVE = !!getTeamClaimAddress();

/**
 * Browse-only Ember League home.
 * Team claiming is locked until the TeamClaim contract is deployed.
 * Teams show as "{City} (Owner has not chosen name)" until a name is
 * submitted and approved by league vote.
 */
export const CryptoHome = () => {
	const lid = useDesktopStore((s) => s.lid);
	const setCryptoMode = useDesktopStore((s) => s.setCryptoMode);
	const setClaimedTeam = useDesktopStore((s) => s.setClaimedTeam);
	const setConnectedAddress = useDesktopStore((s) => s.setConnectedAddress);
	const setView = useDesktopStore((s) => s.setView);
	const openTeam = useDesktopStore((s) => s.openTeam);
	const pushToast = useDesktopStore((s) => s.pushToast);
	const patchLocal = useDesktopStore((s) => s.patchLocal);
	const local = useDesktopStore((s) => s.local);
	const storeClaim = useDesktopStore((s) => s.claimedTeam);

	const teams = useMemo(() => getDefaultTeams() as TeamRow[], []);
	const [address, setAddress] = useState<string>();
	const [selectedTid, setSelectedTid] = useState(0);
	const [proposedName, setProposedName] = useState("");
	const [busy, setBusy] = useState(false);
	const [booting, setBooting] = useState(false);
	const [claim, setClaim] = useState(() => {
		// Don't restore any locally-stored claims made without a real contract tx
		const c = readLocalClaim();
		if (c && !c.txHash) {
			// Clear stale preview claims
			try {
				localStorage.removeItem(CLAIM_STORAGE_KEY);
				sessionStorage.removeItem(CLAIM_STORAGE_KEY);
				document.cookie = `${CLAIM_COOKIE}=; max-age=0; path=/`;
			} catch {}
			return undefined;
		}
		return c;
	});
	const [status, setStatus] = useState<string>();
	const [syncStatus, setSyncStatus] = useState<SyncStatus>(getSyncStatus());
	const [serverVersion, setServerVersion] = useState(0);
	const [serverClaims, setServerClaims] = useState<Record<string, string>>({});
	const syncedRef = useRef(false);

	useEffect(() => {
		setCryptoMode(true);
		const existing = readLocalClaim();
		if (existing?.txHash) {
			setClaim(existing);
			setClaimedTeam(existing);
		}
	}, [setCryptoMode, setClaimedTeam]);

	// Multiplayer sync — start once league is ready
	useEffect(() => {
		if (syncedRef.current) return;
		syncedRef.current = true;

		void (async () => {
			const unsub = onSyncStatus(setSyncStatus);
			const unsubUpdate = onLeagueUpdate((v) => {
				setServerVersion(v);
				// Fetch fresh server claims when the server announces an update
				void fetchClaims().then(setServerClaims);
			});

			// Initialise WebSocket + version check
			const ver = await initLeagueSync();
			setServerVersion(ver);

			// Fetch claims map from server
			const claims = await fetchClaims();
			setServerClaims(claims);

			return () => { unsub(); unsubUpdate(); };
		})();

		return () => {
			destroyLeagueSync();
			syncedRef.current = false;
		};
	}, []);

	const applyClaim = (next: NonNullable<typeof claim>) => {
		setClaim(next);
		setClaimedTeam(next);
		const t = teams.find((x) => x.tid === next.tid);
		if (t) {
			patchLocal({
				userTid: next.tid,
				teamInfo: { region: t.region, name: t.name, abbrev: t.abbrev },
			});
		}
	};

	// Auto-open the shared spectator league
	useEffect(() => {
		if (lid != null) return;
		let cancelled = false;
		setBooting(true);
		void (async () => {
			try {
				const list = (await loadLeagueList()) as any[];
				const existing = list?.find(
					(l) => String(l.name).toLowerCase() === CRYPTO_LEAGUE_NAME.toLowerCase(),
				);
				if (cancelled) return;
				if (existing) {
					await openLeague(existing.lid);
				} else {
					// Fetch the canonical snapshot from the WBBL API worker.
					// Every browser visitor loads this same league — no random generation.
					const snap = await fetchSnapshot();

					if (snap && snap.json) {
						const store = useDesktopStore.getState();
						store.setStatus("Loading WBBL live league…");
						// Inject the snapshot URL into the worker for league creation.
						// We convert the JSON string to a Blob URL so createLeagueFromSnapshot
						// can fetch it via the url parameter.
						const blob = new Blob([snap.json], { type: "application/json" });
						const blobUrl = URL.createObjectURL(blob);
						try {
							const lid = await toWorker("main", "createLeagueFromSnapshot", {
								name: CRYPTO_LEAGUE_NAME,
								snapshotUrl: blobUrl,
							});
							const { saveLeagueMeta } = await import("../util/leagueMeta.ts");
							await saveLeagueMeta(lid, { name: CRYPTO_LEAGUE_NAME });
							await openLeague(lid);
						} finally {
							URL.revokeObjectURL(blobUrl);
						}
					} else {
						// Worker has no snapshot yet — generate a placeholder league.
						// Once you push from the desktop app it will be live for all visitors.
						await createDefaultLeague(CRYPTO_LEAGUE_NAME, 0, {
							abbrev: "WBBL",
							commissioner: "WBBL Commissioner",
						});
					}
				}
				useDesktopStore.getState().setView("dashboard");
			} catch (err) {
				if (!cancelled) setStatus(err instanceof Error ? err.message : String(err));
			} finally {
				if (!cancelled) setBooting(false);
			}
		})();
		return () => { cancelled = true; };
	}, [lid]);

	const onConnect = async () => {
		setBusy(true);
		setStatus(undefined);
		try {
			const addr = await connectWallet();
			setAddress(addr);
			setConnectedAddress(addr);
			pushToast({ text: `Connected ${addr.slice(0, 6)}…${addr.slice(-4)}`, type: "info" });
		} catch (err) {
			setStatus(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	};

	const onClaim = async () => {
		if (busy || !CONTRACT_LIVE) return;
		if (!proposedName.trim()) {
			setStatus("You must choose a team name before claiming.");
			return;
		}
		setBusy(true);
		setStatus(undefined);
		try {
			const addr = address ?? (await connectWallet());
			setAddress(addr);
			setConnectedAddress(addr);
			const txHash = await sendClaimTeamTx(selectedTid, addr);
			const next = {
				address: addr,
				tid: selectedTid,
				txHash,
				proposedName: proposedName.trim(),
				at: Date.now(),
			};
			await registerClaimWithApi(next as any);
			applyClaim(next as any);
			pushToast({
				text: `Claimed ${FRANCHISE_CITIES[selectedTid]}! Name "${proposedName.trim()}" submitted for league vote.`,
				type: "info",
			});
		} catch (err) {
			setStatus(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	};

	const SYNC_LABEL: Record<SyncStatus, string> = {
		live:       "Live",
		connecting: "Connecting…",
		stale:      "Stale",
		offline:    "Offline",
	};

	const activeClaim = claim ?? storeClaim;
	const already = activeClaim != null;

	return (
		<div className="crypto-home">
			<section className="crypto-hero">
				<div className="crypto-hero-copy">
					{/* Red / White / Blue stripe accent */}
					<div className="wbbl-stripe" aria-hidden>
						<div className="wbbl-stripe-bar r" />
						<div className="wbbl-stripe-bar w" />
						<div className="wbbl-stripe-bar b" />
					</div>
					{/* WBBL Basketball Logo */}
					<div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 10 }}>
						<svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 256 256" style={{ flexShrink: 0, filter: "drop-shadow(0 0 14px rgba(200,16,46,0.5))" }}>
							<defs>
								<radialGradient id="ls" cx="36%" cy="30%" r="66%">
									<stop offset="0%" stopColor="#243070"/>
									<stop offset="40%" stopColor="#0d1535"/>
									<stop offset="100%" stopColor="#020510"/>
								</radialGradient>
								<radialGradient id="lr" cx="50%" cy="50%" r="50%">
									<stop offset="78%" stopColor="transparent"/>
									<stop offset="100%" stopColor="#000" stopOpacity="0.75"/>
								</radialGradient>
								<filter id="ln" x="-25%" y="-25%" width="150%" height="150%">
									<feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="b1"/>
									<feGaussianBlur in="SourceGraphic" stdDeviation="1.2" result="b2"/>
									<feMerge><feMergeNode in="b1"/><feMergeNode in="b2"/><feMergeNode in="SourceGraphic"/></feMerge>
								</filter>
								<clipPath id="lc"><circle cx="128" cy="128" r="120"/></clipPath>
							</defs>
							<circle cx="128" cy="128" r="120" fill="url(#ls)"/>
							<g clipPath="url(#lc)" fill="none" stroke="#3a6aff" strokeWidth="0.8" opacity="0.18">
								<ellipse cx="128" cy="128" rx="120" ry="36"/>
								<ellipse cx="128" cy="128" rx="120" ry="72"/>
								<ellipse cx="128" cy="128" rx="36" ry="120"/>
								<ellipse cx="128" cy="128" rx="72" ry="120"/>
							</g>
							<g clipPath="url(#lc)" filter="url(#ln)">
								<path d="M128 8 C182 58,182 198,128 248" fill="none" stroke="#ff1030" strokeWidth="4" strokeLinecap="round"/>
								<path d="M128 8 C74 58,74 198,128 248" fill="none" stroke="#ff1030" strokeWidth="4" strokeLinecap="round"/>
								<path d="M8 128 C58 74,198 74,248 128" fill="none" stroke="#ff1030" strokeWidth="4" strokeLinecap="round"/>
								<path d="M8 128 C58 182,198 182,248 128" fill="none" stroke="#ff1030" strokeWidth="4" strokeLinecap="round"/>
							</g>
							<circle cx="128" cy="128" r="120" fill="url(#lr)"/>
							<circle cx="128" cy="128" r="120" fill="none" stroke="#C8102E" strokeWidth="3" opacity="0.9"/>
							{/* WBBL text overlay */}
							<text
								x="128" y="148"
								textAnchor="middle"
								fontFamily="'Barlow Condensed','Arial Narrow',sans-serif"
								fontWeight="800"
								fontSize="72"
								letterSpacing="2"
								fill="white"
								opacity="0.92"
								style={{ textTransform: "uppercase" }}
							>WBBL</text>
						</svg>
						<div>
							<div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(28px,4vw,48px)", lineHeight: 1, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--white)" }}>
								World Blockchain
							</div>
							<div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(28px,4vw,48px)", lineHeight: 1, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--red-2)", textShadow: "var(--glow-red)" }}>
								Basketball League
							</div>
						</div>
					</div>
					<p className="crypto-eyebrow">
						Emberchain · Chain {EMBER_CHAIN.chainId}
						{" "}·{" "}
						<span className={`sync-dot ${syncStatus}`} title={SYNC_LABEL[syncStatus]} />
						{" "}<span style={{ fontSize: "0.95em", color: "var(--text-3)" }}>
							{SYNC_LABEL[syncStatus]}
						</span>
						{serverVersion > 0 && (
							<span style={{ marginLeft: 8, fontSize: "0.82em", color: "var(--text-3)" }}>
								v{serverVersion}
							</span>
						)}
					</p>
					<p className="crypto-lede">
						A multiplayer basketball universe on-chain. Browse the league freely.
						Each franchise is owned by a wallet — claim yours for{" "}
						<strong>{CLAIM_FEE_EMBR.toString()} EMBR</strong> once the contract
						launches. Your NFT is proof of ownership. All changes by owners
						are broadcast live to every viewer.
					</p>
					<div className="crypto-cta-row">
						{!isWalletAvailable() ? (
							<span className="pill">Wallet required to claim</span>
						) : null}
						<button
							type="button"
							className="btn btn-secondary"
							disabled={busy}
							onClick={() => void onConnect()}
						>
							{address
								? `${address.slice(0, 6)}…${address.slice(-4)}`
								: "Connect wallet"}
						</button>
						{lid != null ? (
							<button
								type="button"
								className="btn btn-primary"
								onClick={() => setView("standings")}
							>
								Browse league
							</button>
						) : null}
					</div>
					{status ? <div className="status-bar error">{status}</div> : null}
					{booting ? <p className="muted">Loading spectator league…</p> : null}
				</div>

				<div className="crypto-claim-panel panel">
					<div className="panel-header">
						<span>Claim a franchise</span>
						<span className={`pill ${CONTRACT_LIVE ? "" : "pill-warning"}`}>
							{CONTRACT_LIVE ? `${CLAIM_FEE_EMBR.toString()} EMBR` : "Contract not yet deployed"}
						</span>
					</div>
					<div className="panel-body">
						{!CONTRACT_LIVE ? (
							<div style={{ padding: "8px 0" }}>
								<p style={{ marginTop: 0, fontWeight: 600 }}>
									Team claiming is not yet open.
								</p>
								<p className="muted" style={{ fontSize: "0.88em" }}>
									The TeamClaim contract has not been deployed to EmberChain yet.
									Once live, you'll be able to pay {CLAIM_FEE_EMBR.toString()} EMBR
									to claim a city, propose a team name, and receive your franchise NFT.
									Name proposals must pass a majority vote from other owners.
								</p>
								<p className="muted" style={{ fontSize: "0.85em" }}>
									Browse all 30 franchises below — all cities are unclaimed.
								</p>
							</div>
						) : already && activeClaim ? (
							<p>
								This wallet claimed{" "}
								<strong>{FRANCHISE_CITIES[activeClaim.tid]}</strong>.
								{(activeClaim as any).proposedName ? (
									<span>
										{" "}Name proposal "<em>{(activeClaim as any).proposedName}</em>"
										is pending league vote.
									</span>
								) : null}
							</p>
						) : (
							<>
								<div className="field">
									<label htmlFor="claim-city">City (franchise)</label>
									<select
										id="claim-city"
										value={selectedTid}
										onChange={(e) => setSelectedTid(Number(e.target.value))}
									>
										{Object.entries(FRANCHISE_CITIES).map(([tid, city]) => (
											<option key={tid} value={tid}>{city}</option>
										))}
									</select>
								</div>
								<div className="field" style={{ marginTop: 10 }}>
									<label htmlFor="propose-name">
										Proposed team name{" "}
										<span className="muted" style={{ fontSize: "0.82em", fontWeight: 400 }}>
											(must pass league vote to become official)
										</span>
									</label>
									<input
										id="propose-name"
										type="text"
										maxLength={30}
										placeholder="e.g. Hammers"
										value={proposedName}
										onChange={(e) => setProposedName(e.target.value)}
										style={{ width: "100%", padding: "5px 8px", borderRadius: 5, border: "1px solid var(--border)", background: "var(--bg-secondary)", color: "inherit" }}
									/>
								</div>
								<p className="muted" style={{ fontSize: "0.82em", marginTop: 6 }}>
									One franchise per wallet · one claim per IP + device.
									Name changes cost EMBR and need majority owner approval.
									Cities may also be changed (relocation) by league vote.
								</p>
								<button
									type="button"
									className="btn btn-primary"
									disabled={busy || already || !proposedName.trim()}
									onClick={() => void onClaim()}
								>
									{busy ? "…" : `Claim for ${CLAIM_FEE_EMBR.toString()} EMBR`}
								</button>
							</>
						)}
						{activeClaim?.txHash ? (
							<p className="muted" style={{ fontSize: "0.82em", marginTop: 10 }}>
								Tx: {activeClaim.txHash.slice(0, 14)}…
							</p>
						) : null}
					</div>
				</div>
			</section>

			<section className="panel" style={{ marginTop: 18 }}>
				<div className="panel-header">
					<span>All 30 Franchises</span>
					<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
						<span className={`sync-dot ${syncStatus}`} />
						<span className="pill">
							{local.season ?? "—"} · {local.phaseText ?? "Browse"}
						</span>
					</div>
				</div>
				<div className="panel-body">
					<p className="muted" style={{ marginTop: 0, fontSize: "0.88em" }}>
						Franchises show their claimed status in real-time.
						Cities are locked; team names are chosen by owners and must pass a league vote.
					</p>
					<div className="crypto-team-grid">
						{Object.entries(FRANCHISE_CITIES).map(([tidStr, city]) => {
							const tid = Number(tidStr);
							const t = teams[tid];
							const owner = serverClaims[tidStr];
							const isMine = owner?.toLowerCase() === address?.toLowerCase();
							const chipClass = [
								"crypto-team-chip",
								owner ? "claimed" : "",
								isMine ? "mine" : "",
							].filter(Boolean).join(" ");
							return (
								<button
									key={tid}
									type="button"
									className={chipClass}
									onClick={() => openTeam(tid)}
								>
									<span className="abbrev">{t?.abbrev ?? tidStr}</span>
									<span style={{ fontSize: "0.85em" }}>{city}</span>
									<span style={{ fontSize: "0.72em", color: isMine ? "var(--gold)" : "var(--text-3)", display: "block" }}>
										{isMine
											? "Your franchise"
											: owner
											? `Owned · ${owner.slice(0, 6)}…${owner.slice(-4)}`
											: "Owner has not chosen name"}
									</span>
								</button>
							);
						})}
					</div>
				</div>
			</section>
		</div>
	);
};
